import AIAgentCard from "../sidebar-widgets/AIAgentCard";
import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Call,
  MessageText1,
  Sms,
} from "iconsax-react";
import { SequenceActionIcon } from "../ActionButtonIcons";
import {
  compactFilledPillButtonSx,
  PILL_BUTTON_BG,
  pillButtonInteractiveSx,
  quickActionPillButtonSx,
  withPillButtonClassName,
} from "../../utils/pillButtonStyles";
import { SIDEBAR_WIDGET_TITLE_SX } from "../sidebar-widgets/sidebarWidgetStyles";

const PURPLE = PILL_BUTTON_BG;
const BORDER = "#E5E7EB";
const TEXT = "#344054";
const MUTED = "#98A2B3";

const cardSx = {
  backgroundColor: "#fff",
  border: `1px solid ${BORDER}`,
  borderRadius: 3,
  p: 2,
};

const actionButtonSx = {
  ...compactFilledPillButtonSx,
  height: 38,
  px: 2.2,
  fontSize: 12,
  fontWeight: 400,
};

const quickActionButtonSx = {
  ...quickActionPillButtonSx,
  height: 48,
  px: 2.8,
  fontSize: 14,
};

function WidgetTitle({ children }) {
  return (
    <Typography
      className="heading"
      sx={{
        ...SIDEBAR_WIDGET_TITLE_SX,
        mb: 1.75,
      }}
    >
      {children}
    </Typography>
  );
}

function MessageActionChip({ label, icon, onClick, variant = "soft" }) {
  const isFilled = variant === "filled";

  return (
    <Button
      type="button"
      className={withPillButtonClassName()}
      onClick={onClick}
      startIcon={icon || null}
      sx={[
        pillButtonInteractiveSx,
        {
          minWidth: "auto",
          height: isFilled ? 34 : 32,
          px: isFilled ? 1.8 : 1.7,
          borderRadius: 999,
          textTransform: "none",
          fontSize: 11.5,
          fontWeight: 400,
          color: "#fff",
          backgroundColor: PURPLE,
          border: `1px solid ${PURPLE}`,
          boxShadow: "none",
          "& .MuiButton-startIcon": {
            marginRight: 0.55,
            "& > *": {
              fontSize: "14px !important",
            },
          },
          "&:hover": {
            backgroundColor: PURPLE,
            boxShadow: "none",
            borderColor: PURPLE,
          },
        },
      ]}
    >
      {label}
    </Button>
  );
}

export function MessagesQuickActionsCard({
  onCall,
  onEmail,
  onSchedule,
  onCreateSequence,
  sx = {},
}) {
  return (
    <Box sx={{ ...cardSx, ...sx }}>
      <WidgetTitle>Quick Actions</WidgetTitle>

      <Stack direction="row" flexWrap="wrap" gap={1.25}>
        <Button
          className={withPillButtonClassName()}
          startIcon={<Call size="16" color="#fff" />}
          sx={[pillButtonInteractiveSx, quickActionButtonSx]}
          onClick={onCall}
        >
          Call
        </Button>
        <Button
          className={withPillButtonClassName()}
          startIcon={<Sms size="16" color="#fff" />}
          sx={[pillButtonInteractiveSx, quickActionButtonSx]}
          onClick={onEmail}
        >
          Email
        </Button>
        <Button
          className={withPillButtonClassName()}
          startIcon={<MessageText1 size="16" color="#fff" />}
          sx={[pillButtonInteractiveSx, quickActionButtonSx]}
          onClick={onSchedule}
        >
          Schedule
        </Button>
        <Button
          className={withPillButtonClassName()}
          startIcon={<SequenceActionIcon size={16} />}
          sx={[pillButtonInteractiveSx, quickActionButtonSx]}
          onClick={onCreateSequence}
        >
          Create Sequence
        </Button>
      </Stack>
    </Box>
  );
}

export function MessagesAIAgentCard({ selectedMessage, sx = {} }) {
  return <AIAgentCard sx={sx} entityType="message" entityId={selectedMessage?.id}
    entityName={selectedMessage?.subject || selectedMessage?.candidate_name || "Selected message"}
    promptText="Review this message and help plan a response."
    noteText="Ask for a summary, draft a reply, or attach supporting files."
    linkText={selectedMessage?.linkedin_url || ""}
    contextText={JSON.stringify({ subject: selectedMessage?.subject, preview: selectedMessage?.preview_text, contact: selectedMessage?.candidate_name })} />;
}

export function MessagesWriteEmailCard({
  message,
  onSend,
  sending = false,
  sx = {},
}) {
  const defaults = useMemo(
    () => ({
      recipient: message?.candidate_email || "",
      subject: message?.subject || "",
      priority: message?.priority || "High",
      message: message?.preview_text || "",
    }),
    [message]
  );

  const [form, setForm] = useState(defaults);

  useEffect(() => {
    setForm(defaults);
  }, [defaults]);

  const updateField = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleClear = () => setForm(defaults);

  return (
    <Box sx={{ ...cardSx, ...sx }}>
      <WidgetTitle>Write a Email</WidgetTitle>

      <Stack spacing={1.1}>
        <Select
          size="small"
          value={form.recipient}
          onChange={updateField("recipient")}
          displayEmpty
          sx={{ borderRadius: 2 }}
        >
          <MenuItem value="">Recipient</MenuItem>
          {message?.candidate_email ? (
            <MenuItem value={message.candidate_email}>{message.candidate_email}</MenuItem>
          ) : null}
        </Select>

        <TextField
          size="small"
          placeholder="Subject"
          value={form.subject}
          onChange={updateField("subject")}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        />

        <Select
          size="small"
          value={form.priority}
          onChange={updateField("priority")}
          sx={{ borderRadius: 2 }}
        >
          <MenuItem value="Low">Low</MenuItem>
          <MenuItem value="Medium">Medium</MenuItem>
          <MenuItem value="High">High</MenuItem>
        </Select>

        <TextField
          multiline
          minRows={4}
          placeholder="Write your message..."
          value={form.message}
          onChange={updateField("message")}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        />

        <Box
          sx={{
            border: `1px dashed ${BORDER}`,
            borderRadius: 2,
            minHeight: 68,
            px: 1.5,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            color: MUTED,
            fontSize: 12,
          }}
        >
          Click to upload or drag and drop
          <br />
          SVG, PNG, JPG or PDF
        </Box>
      </Stack>

      <Stack direction="row" justifyContent="flex-end" spacing={1.2} sx={{ mt: 1.5 }}>
        <Button
          type="button"
          variant="outlined"
          className={withPillButtonClassName()}
          onClick={handleClear}
          sx={[
            pillButtonInteractiveSx,
            {
              textTransform: "none",
              borderRadius: 999,
              borderColor: BORDER,
              color: MUTED,
            },
          ]}
        >
          Close
        </Button>
        <Button
          type="button"
          className={withPillButtonClassName()}
          onClick={() => onSend?.(form)}
          disabled={sending || !form.recipient}
          sx={[pillButtonInteractiveSx, actionButtonSx, { height: 34, minWidth: 88, px: 2.2 }]}
        >
          {sending ? "Sending..." : "Send"}
        </Button>
      </Stack>
    </Box>
  );
}

export function MessagesChatCard({ chats = [], onViewAll, sx = {} }) {
  return (
    <Box sx={{ ...cardSx, ...sx }}>
      <WidgetTitle>Chat</WidgetTitle>

      <Stack spacing={1.3}>
        {chats.map((chat) => (
          <Box key={chat.id} sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Avatar sx={{ width: 34, height: 34, fontSize: 13 }}>
              {String(chat.name || "?").trim().charAt(0).toUpperCase()}
            </Avatar>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                <Typography sx={{ color: TEXT, fontSize: 12.5, fontWeight: 700 }}>
                  {chat.name}
                </Typography>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: chat.status === "online" ? "#22C55E" : "#CBD5E1",
                  }}
                />
              </Box>
              <Typography sx={{ color: MUTED, fontSize: 11.5 }}>{chat.role}</Typography>
              <Typography
                sx={{
                  color: TEXT,
                  fontSize: 11.5,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {chat.preview}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>

      <Divider sx={{ my: 1.6 }} />

      <Box sx={{ textAlign: "right" }}>
        <Button
          type="button"
          onClick={onViewAll}
          sx={{
            minWidth: "auto",
            px: 0,
            textTransform: "none",
            fontSize: 12,
            fontWeight: 700,
            color: PURPLE,
            "&:hover": { backgroundColor: "transparent" },
          }}
        >
          View all Chats
        </Button>
      </Box>
    </Box>
  );
}

export function MessageComposerAssistRow({
  onAi,
  onTranslate,
  onSchedule,
  onRewrite,
  items,
  variant = "soft",
  rightAction,
  sx = {},
}) {
  const resolvedItems =
    Array.isArray(items) && items.length
      ? items
      : [
          { key: "ai", label: "AI Agent", onClick: onAi },
          { key: "translate", label: "Translate", onClick: onTranslate },
          { key: "schedule", label: "Schedule", onClick: onSchedule },
          { key: "rewrite", label: "Rewrite", onClick: onRewrite },
        ];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        ...sx,
      }}
    >
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {resolvedItems.map((item) => (
          <MessageActionChip
            key={item.key || item.label}
            label={item.label}
            icon={item.icon}
            onClick={item.onClick}
            variant={variant}
          />
        ))}
      </Stack>

      {rightAction ? (
        <IconButton
          size="small"
          onClick={rightAction.onClick}
          sx={{
            p: 0.3,
            color: PURPLE,
            flexShrink: 0,
            ml: 0.4,
          }}
        >
          {rightAction.icon}
        </IconButton>
      ) : null}
    </Box>
  );
}
